import React from "react";
import { Form, Button, Row, Col, Card, Accordion } from "react-bootstrap";

/**
 * Visual editor for course pageContent — no JSON knowledge needed.
 *
 * Expected shape:
 * {
 *   heroTitle: string,
 *   introParagraphs: string[],
 *   sections: [{ title, introText?, listType, items: [{ title?, description?, text?, subItems?: string[] }] }],
 *   sidebar: { courseTitle, language, level, duration, certifications },
 *   flipBookPages: string[]
 * }
 */

const EMPTY_SECTION = {
  title: "",
  introText: "",
  listType: "unordered",
  items: [],
};
const EMPTY_ITEM = { title: "", description: "", text: "", subItems: [] };

const fieldStyle = { borderRadius: "12px", marginBottom: "6px" };
const cardStyle = {
  border: "1px solid #dee2e6",
  borderRadius: "12px",
  marginBottom: "12px",
  padding: "12px",
  backgroundColor: "#fafbfc",
};
const sectionCardStyle = {
  border: "1px solid #b8daff",
  borderRadius: "12px",
  marginBottom: "14px",
  padding: "14px",
  backgroundColor: "#f0f7ff",
};
const deleteBtn = {
  fontSize: "12px",
  padding: "2px 10px",
  borderRadius: "12px",
};
const addBtn = {
  fontSize: "13px",
  padding: "4px 14px",
  borderRadius: "16px",
  fontWeight: "500",
};

const PageContentEditor = ({ value, onChange }) => {
  // Parse existing value
  let pc = {};
  if (typeof value === "string" && value.trim()) {
    try {
      pc = JSON.parse(value);
    } catch {
      // If invalid JSON, start fresh
      pc = {};
    }
  } else if (typeof value === "object" && value !== null) {
    pc = value;
  }

  const heroTitle = pc.heroTitle || "";
  const introParagraphs = Array.isArray(pc.introParagraphs)
    ? pc.introParagraphs
    : [];
  const sections = Array.isArray(pc.sections) ? pc.sections : [];
  const sidebar = pc.sidebar || {};
  const flipBookPages = Array.isArray(pc.flipBookPages) ? pc.flipBookPages : [];

  // Emit updated JSON string
  const emit = (updated) => {
    onChange(JSON.stringify(updated, null, 2));
  };

  const update = (key, val) => {
    emit({ ...pc, [key]: val });
  };

  /* ---------- Intro Paragraphs ---------- */
  const updatePara = (idx, text) => {
    const arr = [...introParagraphs];
    arr[idx] = text;
    update("introParagraphs", arr);
  };
  const addPara = () => update("introParagraphs", [...introParagraphs, ""]);
  const removePara = (idx) =>
    update(
      "introParagraphs",
      introParagraphs.filter((_, i) => i !== idx),
    );

  /* ---------- Sections ---------- */
  const updateSection = (idx, key, val) => {
    const arr = [...sections];
    arr[idx] = { ...arr[idx], [key]: val };
    update("sections", arr);
  };
  const addSection = () =>
    update("sections", [...sections, { ...EMPTY_SECTION, items: [] }]);
  const removeSection = (idx) =>
    update(
      "sections",
      sections.filter((_, i) => i !== idx),
    );
  const moveSection = (idx, dir) => {
    const arr = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    update("sections", arr);
  };

  /* ---------- Items within a section ---------- */
  const updateItem = (sIdx, iIdx, key, val) => {
    const arr = [...sections];
    const items = [...(arr[sIdx].items || [])];
    items[iIdx] = { ...items[iIdx], [key]: val };
    arr[sIdx] = { ...arr[sIdx], items };
    update("sections", arr);
  };
  const addItem = (sIdx) => {
    const arr = [...sections];
    arr[sIdx] = {
      ...arr[sIdx],
      items: [...(arr[sIdx].items || []), { ...EMPTY_ITEM, subItems: [] }],
    };
    update("sections", arr);
  };
  const removeItem = (sIdx, iIdx) => {
    const arr = [...sections];
    arr[sIdx] = {
      ...arr[sIdx],
      items: arr[sIdx].items.filter((_, i) => i !== iIdx),
    };
    update("sections", arr);
  };

  /* ---------- Sub-items ---------- */
  const updateSubItem = (sIdx, iIdx, subIdx, val) => {
    const arr = [...sections];
    const items = [...arr[sIdx].items];
    const subs = [...(items[iIdx].subItems || [])];
    subs[subIdx] = val;
    items[iIdx] = { ...items[iIdx], subItems: subs };
    arr[sIdx] = { ...arr[sIdx], items };
    update("sections", arr);
  };
  const addSubItem = (sIdx, iIdx) => {
    const arr = [...sections];
    const items = [...arr[sIdx].items];
    items[iIdx] = {
      ...items[iIdx],
      subItems: [...(items[iIdx].subItems || []), ""],
    };
    arr[sIdx] = { ...arr[sIdx], items };
    update("sections", arr);
  };
  const removeSubItem = (sIdx, iIdx, subIdx) => {
    const arr = [...sections];
    const items = [...arr[sIdx].items];
    items[iIdx] = {
      ...items[iIdx],
      subItems: items[iIdx].subItems.filter((_, i) => i !== subIdx),
    };
    arr[sIdx] = { ...arr[sIdx], items };
    update("sections", arr);
  };

  /* ---------- Sidebar ---------- */
  const updateSidebar = (key, val) => {
    update("sidebar", { ...sidebar, [key]: val });
  };

  /* ---------- FlipBook Pages ---------- */
  const updateFlip = (idx, val) => {
    const arr = [...flipBookPages];
    arr[idx] = val;
    update("flipBookPages", arr);
  };
  const addFlip = () => update("flipBookPages", [...flipBookPages, ""]);
  const removeFlip = (idx) =>
    update(
      "flipBookPages",
      flipBookPages.filter((_, i) => i !== idx),
    );

  return (
    <div style={{ marginTop: "10px" }}>
      {/* ===== Hero Title ===== */}
      <Form.Group controlId="pc_heroTitle" style={{ marginBottom: "12px" }}>
        <Form.Label style={{ fontWeight: "600" }}>
          🏷️ Page Heading (Hero Title)
        </Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Prarambh – Foundation Level"
          value={heroTitle}
          onChange={(e) => update("heroTitle", e.target.value)}
          style={fieldStyle}
        />
        <Form.Text className="text-muted">
          The big heading shown at the top of the course page.
        </Form.Text>
      </Form.Group>

      {/* ===== Intro Paragraphs ===== */}
      <div style={{ marginBottom: "14px" }}>
        <Form.Label style={{ fontWeight: "600" }}>
          📝 Introduction Paragraphs
        </Form.Label>
        <Form.Text
          className="text-muted d-block"
          style={{ marginBottom: "6px" }}
        >
          Short paragraphs shown below the heading. Add as many as you want.
        </Form.Text>
        {introParagraphs.map((para, idx) => (
          <div
            key={idx}
            style={{ display: "flex", gap: "6px", marginBottom: "6px" }}
          >
            <Form.Control
              as="textarea"
              rows={2}
              value={para}
              onChange={(e) => updatePara(idx, e.target.value)}
              placeholder={`Paragraph ${idx + 1}`}
              style={{ ...fieldStyle, flex: 1 }}
            />
            <Button
              variant="outline-danger"
              size="sm"
              style={deleteBtn}
              onClick={() => removePara(idx)}
              title="Remove paragraph"
            >
              ✕
            </Button>
          </div>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          style={addBtn}
          onClick={addPara}
        >
          + Add Paragraph
        </Button>
      </div>

      {/* ===== Sections ===== */}
      <div style={{ marginBottom: "14px" }}>
        <Form.Label style={{ fontWeight: "600", fontSize: "15px" }}>
          📋 Content Sections
        </Form.Label>
        <Form.Text
          className="text-muted d-block"
          style={{ marginBottom: "8px" }}
        >
          Each section has a title, optional intro text, and a list of items.
          You can reorder, add, or remove sections.
        </Form.Text>

        {sections.map((section, sIdx) => (
          <div key={sIdx} style={sectionCardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <strong style={{ fontSize: "14px" }}>Section {sIdx + 1}</strong>
              <div style={{ display: "flex", gap: "4px" }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={deleteBtn}
                  onClick={() => moveSection(sIdx, -1)}
                  disabled={sIdx === 0}
                  title="Move up"
                >
                  ↑
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={deleteBtn}
                  onClick={() => moveSection(sIdx, 1)}
                  disabled={sIdx === sections.length - 1}
                  title="Move down"
                >
                  ↓
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={deleteBtn}
                  onClick={() => removeSection(sIdx)}
                  title="Delete section"
                >
                  ✕ Remove
                </Button>
              </div>
            </div>

            <Form.Group style={{ marginBottom: "6px" }}>
              <Form.Label style={{ fontWeight: "500", fontSize: "13px" }}>
                Section Title
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Course Structure"
                value={section.title || ""}
                onChange={(e) => updateSection(sIdx, "title", e.target.value)}
                style={fieldStyle}
              />
            </Form.Group>

            <Form.Group style={{ marginBottom: "6px" }}>
              <Form.Label style={{ fontWeight: "500", fontSize: "13px" }}>
                Intro Text (optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional intro paragraph for this section"
                value={section.introText || ""}
                onChange={(e) =>
                  updateSection(sIdx, "introText", e.target.value)
                }
                style={fieldStyle}
              />
            </Form.Group>

            <Form.Group style={{ marginBottom: "10px" }}>
              <Form.Label style={{ fontWeight: "500", fontSize: "13px" }}>
                List Style
              </Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="• Bullet List"
                  name={`listType-${sIdx}`}
                  checked={(section.listType || "unordered") === "unordered"}
                  onChange={() => updateSection(sIdx, "listType", "unordered")}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="1. Numbered List"
                  name={`listType-${sIdx}`}
                  checked={section.listType === "ordered"}
                  onChange={() => updateSection(sIdx, "listType", "ordered")}
                />
              </div>
            </Form.Group>

            {/* Items */}
            <div style={{ marginLeft: "10px" }}>
              <Form.Label style={{ fontWeight: "500", fontSize: "13px" }}>
                List Items
              </Form.Label>
              {(section.items || []).map((item, iIdx) => (
                <div key={iIdx} style={cardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <small style={{ fontWeight: "500", color: "#666" }}>
                      Item {iIdx + 1}
                    </small>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      style={deleteBtn}
                      onClick={() => removeItem(sIdx, iIdx)}
                    >
                      ✕
                    </Button>
                  </div>

                  <Row>
                    <Col xs={6}>
                      <Form.Control
                        type="text"
                        placeholder="Item title (bold text)"
                        value={item.title || ""}
                        onChange={(e) =>
                          updateItem(sIdx, iIdx, "title", e.target.value)
                        }
                        style={{ ...fieldStyle, fontSize: "13px" }}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Control
                        type="text"
                        placeholder="Short text (inline)"
                        value={item.text || ""}
                        onChange={(e) =>
                          updateItem(sIdx, iIdx, "text", e.target.value)
                        }
                        style={{ ...fieldStyle, fontSize: "13px" }}
                      />
                    </Col>
                  </Row>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Description (paragraph below title)"
                    value={item.description || ""}
                    onChange={(e) =>
                      updateItem(sIdx, iIdx, "description", e.target.value)
                    }
                    style={{ ...fieldStyle, fontSize: "13px" }}
                  />

                  {/* Sub-items */}
                  {(item.subItems || []).length > 0 && (
                    <div
                      style={{
                        marginLeft: "16px",
                        marginTop: "4px",
                        marginBottom: "4px",
                      }}
                    >
                      <small style={{ color: "#888" }}>Sub-items:</small>
                      {item.subItems.map((sub, subIdx) => (
                        <div
                          key={subIdx}
                          style={{
                            display: "flex",
                            gap: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <Form.Control
                            type="text"
                            value={sub}
                            onChange={(e) =>
                              updateSubItem(sIdx, iIdx, subIdx, e.target.value)
                            }
                            placeholder={`Sub-item ${subIdx + 1}`}
                            style={{ ...fieldStyle, fontSize: "12px", flex: 1 }}
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={deleteBtn}
                            onClick={() => removeSubItem(sIdx, iIdx, subIdx)}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="outline-info"
                    size="sm"
                    style={{ ...addBtn, fontSize: "11px", marginTop: "2px" }}
                    onClick={() => addSubItem(sIdx, iIdx)}
                  >
                    + Sub-item
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-success"
                size="sm"
                style={addBtn}
                onClick={() => addItem(sIdx)}
              >
                + Add Item
              </Button>
            </div>
          </div>
        ))}

        <Button variant="outline-primary" style={addBtn} onClick={addSection}>
          + Add Section
        </Button>
      </div>

      {/* ===== Sidebar ===== */}
      <div style={{ marginBottom: "14px" }}>
        <Form.Label style={{ fontWeight: "600" }}>
          📊 Sidebar Info (right panel on the page)
        </Form.Label>
        <Form.Text
          className="text-muted d-block"
          style={{ marginBottom: "6px" }}
        >
          If left empty, values from the fields above (Level, Duration, etc.)
          will be used automatically.
        </Form.Text>
        <Row>
          <Col xs={6}>
            <Form.Control
              type="text"
              placeholder="Sidebar course title (optional)"
              value={sidebar.courseTitle || ""}
              onChange={(e) => updateSidebar("courseTitle", e.target.value)}
              style={fieldStyle}
            />
          </Col>
          <Col xs={6}>
            <Form.Control
              type="text"
              placeholder="Language override (optional)"
              value={sidebar.language || ""}
              onChange={(e) => updateSidebar("language", e.target.value)}
              style={fieldStyle}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={4}>
            <Form.Control
              type="text"
              placeholder="Level override"
              value={sidebar.level || ""}
              onChange={(e) => updateSidebar("level", e.target.value)}
              style={fieldStyle}
            />
          </Col>
          <Col xs={4}>
            <Form.Control
              type="text"
              placeholder="Duration override"
              value={sidebar.duration || ""}
              onChange={(e) => updateSidebar("duration", e.target.value)}
              style={fieldStyle}
            />
          </Col>
          <Col xs={4}>
            <Form.Control
              type="text"
              placeholder="Certifications (e.g. Yes)"
              value={sidebar.certifications || ""}
              onChange={(e) => updateSidebar("certifications", e.target.value)}
              style={fieldStyle}
            />
          </Col>
        </Row>
      </div>

      {/* ===== FlipBook Pages ===== */}
      <div style={{ marginBottom: "10px" }}>
        <Form.Label style={{ fontWeight: "600" }}>
          📖 FlipBook Pages (image URLs)
        </Form.Label>
        <Form.Text
          className="text-muted d-block"
          style={{ marginBottom: "6px" }}
        >
          Add image URLs for each page of the interactive flipbook. Leave empty
          if no flipbook needed.
        </Form.Text>
        {flipBookPages.map((url, idx) => (
          <div
            key={idx}
            style={{ display: "flex", gap: "6px", marginBottom: "4px" }}
          >
            <span
              style={{
                minWidth: "28px",
                textAlign: "center",
                paddingTop: "6px",
                fontSize: "12px",
                color: "#888",
              }}
            >
              {idx + 1}
            </span>
            <Form.Control
              type="text"
              value={url}
              onChange={(e) => updateFlip(idx, e.target.value)}
              placeholder="https://example.com/page1.jpg"
              style={{ ...fieldStyle, flex: 1, fontSize: "13px" }}
            />
            <Button
              variant="outline-danger"
              size="sm"
              style={deleteBtn}
              onClick={() => removeFlip(idx)}
            >
              ✕
            </Button>
          </div>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          style={addBtn}
          onClick={addFlip}
        >
          + Add Page
        </Button>
      </div>
    </div>
  );
};

export default PageContentEditor;
